"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ensureLocalStorage = exports.createMemoryStorage = exports.safeLocalStorage = void 0;
const readStorage = () => {
    try {
        return globalThis.localStorage ?? null;
    }
    catch {
        // Reading `localStorage` itself can throw in sandboxed/blocked contexts.
        return null;
    }
};
/**
 * Null- and throw-safe wrapper around `localStorage`. Every method degrades to
 * a no-op (reads return null) when storage is missing or blocked, instead of
 * throwing "null is not an object (evaluating 'localStorage.getItem')"
 * (Sentry PERFORMANCE-WEB-6VM / PERFORMANCE-WEB-6VK). Prefer this over touching
 * `localStorage` directly, especially in code that can run in PDF/prerender
 * contexts.
 */
exports.safeLocalStorage = {
    getItem(key) {
        try {
            return readStorage()?.getItem(key) ?? null;
        }
        catch {
            return null;
        }
    },
    setItem(key, value) {
        try {
            readStorage()?.setItem(key, value);
        }
        catch {
            // Writes are best-effort; ignore failures in blocked contexts.
        }
    },
    removeItem(key) {
        try {
            readStorage()?.removeItem(key);
        }
        catch {
            // ignore
        }
    },
    clear() {
        try {
            readStorage()?.clear();
        }
        catch {
            // ignore
        }
    }
};
const createMemoryStorage = () => {
    const store = new Map();
    return {
        getItem: (key) => (store.has(key) ? store.get(key) : null),
        setItem: (key, value) => {
            store.set(key, String(value));
        },
        removeItem: (key) => {
            store.delete(key);
        },
        clear: () => {
            store.clear();
        }
    };
};
exports.createMemoryStorage = createMemoryStorage;
const isNativeStorageWorking = (win) => {
    try {
        const probeKey = '__ls_probe__';
        win.localStorage.setItem(probeKey, probeKey);
        win.localStorage.removeItem(probeKey);
        return true;
    }
    catch {
        return false;
    }
};
/**
 * Install an in-memory `localStorage` fallback when the real one is missing or
 * blocked (PDF/prerender contexts, privacy modes), so the app's many unguarded
 * `localStorage.*` calls degrade to a no-op store instead of crashing the page.
 * Idempotent and safe to call once at app entry. Guards Sentry
 * PERFORMANCE-WEB-6VM / PERFORMANCE-WEB-6VK.
 */
const ensureLocalStorage = () => {
    if (typeof window === 'undefined')
        return;
    if (isNativeStorageWorking(window))
        return;
    try {
        Object.defineProperty(window, 'localStorage', {
            configurable: true,
            writable: true,
            value: (0, exports.createMemoryStorage)()
        });
    }
    catch {
        // If the property can't be redefined, leave the environment untouched.
    }
};
exports.ensureLocalStorage = ensureLocalStorage;
